import type { Carer, Resident, RiskAssessment } from '@lmc/ts-common'
import dayjs from 'dayjs'
import React, { Component, type CSSProperties } from 'react'
import { connect } from 'react-redux'
import { groupDependentFields } from '../../common/utils'
import { formatWithResidentInfo } from '../../selectors/helpers/InitialAssessments/formatFieldOutput'
import PdfExport from './PdfExport'
import { Header, PageNumbers, RiskOutcomeScore, Title } from './pdfContent'
import { formatFormField } from './pdfForms'
import { tableStyles } from './styles'
import { splitString } from './utils'

interface Props {
  carer: Carer
  riskAssessment: RiskAssessment
  resident: Resident
  disabled: boolean
  style: CSSProperties
  title: string
  carers: Carer[]
  preferredPeopleNominative?: string
}

class RiskAssessmentPdfExport extends Component<Props> {
  formatDate = (date: string) => dayjs(date).format('DD-MM-YYYY')

  formatTime = (time: string) => dayjs(time).format('HH:mm')

  getName = (item: Resident) => `${item.name.first} ${item.name.last}`

  getCarerName = (carerId: string) => {
    const { carers } = this.props
    const carer = carers?.find((carer) => carer.id === carerId)
    return carer ? `${carer.name?.first} ${carer.name?.last}` : carerId
  }

  formatArchiveDate = (isoDate: string | Date) => {
    return dayjs(isoDate).format('DD-MM-YYYY HH:mm')
  }

  formatLabelsWithResidentInfo = (formFields: any[], resident: Resident) => {
    const { preferredPeopleNominative } = this.props
    if (!formFields || !resident) return formFields
    return formFields.map((field) => ({
      ...field,
      label: formatWithResidentInfo(
        field.label,
        resident,
        preferredPeopleNominative
      )
    }))
  }

  // @ts-ignore
  // @ts-nocheck
  getRiskAssessmentDoc = () => {
    const {
      carer,
      riskAssessment: {
        createdOn,
        reviewedOn,
        form: { formFields, name },
        formData,
        riskLevel,
        riskScore,
        // TODO: fix type in integration ticket
        //@ts-ignore
        archiveMetadata
      },
      resident
    } = this.props

    if (!formData || !formFields) return null

    const formFieldsWithResidentInfo = this.formatLabelsWithResidentInfo(
      formFields,
      resident
    )
    const withDependentFields = groupDependentFields(formFieldsWithResidentInfo)
    const lastUpdateText = `${this.formatDate(
      // @ts-ignore
      reviewedOn || createdOn
      // @ts-ignore
    )} at ${this.formatTime(reviewedOn || createdOn)} - ${this.getName(carer)}`

    let content: any[] = []

    content = [
      // @ts-ignore
      Header({
        title: `${this.getName(resident)} - ${name}`
      })
    ]

    if (archiveMetadata) {
      const carerName = this.getCarerName(archiveMetadata.archivedBy)
      const formattedDate = this.formatArchiveDate(archiveMetadata.archivedAt)

      if (carerName && formattedDate && archiveMetadata.reason) {
        content = [
          ...content,
          {
            text: `This was archived by ${carerName} on ${formattedDate}.`,
            textStyle: 'tableHeaderSmall',
            margin: [0, 0, 0, 14]
          },
          {
            text: `Reason: ${archiveMetadata.reason}`,
            textStyle: 'tableHeaderSmall',
            margin: [0, 0, 0, 30]
          }
        ]
      }
    }

    content = [
      ...content,
      Title({ title: 'Last update', style: 'h2' }),
      {
        text: lastUpdateText,
        textStyle: 'tableHeaderSmall',
        margin: [0, 0, 0, 30]
      }
    ]

    if (riskLevel || formData.actions) {
      content = [
        ...content,
        {
          columns: [
            riskLevel
              ? [
                  Title({ title: 'Outcome Risk Score', style: 'h2' }),
                  // @ts-ignore
                  RiskOutcomeScore(riskLevel, riskScore)
                ]
              : null,
            formData.actions
              ? [
                  Title({ title: 'Actions', style: 'h2' }),
                  {
                    text: splitString(formData.actions as string, 20),
                    style: 'tableHeaderSmall'
                  }
                ]
              : null
          ],
          margin: [0, 0, 0, 30]
        }
      ]
    }

    return {
      content: [
        ...content,
        ...withDependentFields.map((field) =>
          formatFormField({ formData, ...field })
        )
      ],
      footer: PageNumbers,
      styles: tableStyles
    }
  }

  getDocDefinition = () => {
    const { carer, riskAssessment, resident } = this.props
    return new Promise((resolve, reject) => {
      if (!carer || !riskAssessment || !riskAssessment.form || !resident) {
        reject('Data not provided')
      } else {
        resolve(this.getRiskAssessmentDoc())
      }
    })
  }

  render() {
    const { disabled, style, title } = this.props
    return (
      <PdfExport
        getDocDefinition={this.getDocDefinition}
        pdfTitle={title}
        style={style}
        // @ts-ignore
        disabled={disabled}
      />
    )
  }
}

const mapStateToProps = (state: {
  data: { carers: Carer[] }
  global: { home: { preferredPeopleNominative?: string } }
}) => ({
  carers: state.data.carers,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative
})

export default connect(mapStateToProps)(RiskAssessmentPdfExport)
